/**
 * Pivotea filas largas (una fila por grupo + bucket de fecha + valor) en
 * filas anchas (una fila por grupo, una columna por cada valor del bucket).
 * Reusa el dataset y la tabla existentes: la columna "Total" de DataTable ya
 * suma todas las columnas numericas de la fila, asi que sirve directo como
 * columna de totales sin codigo adicional (ver DataTable.js, showTotalColumn).
 *
 * Soporta los buckets de fecha con un conjunto de columnas fijo y conocido:
 *  - hourOfDay   -> "00".."23"
 *  - dayOfWeek   -> "Lunes".."Domingo"
 *  - dayOfMonth  -> "1".."31"
 *  - monthOfYear -> "Enero".."Diciembre"
 */
import { DAY_OF_WEEK_NAMES_ES, MONTH_NAMES_ES } from './dateBucketFormat';

function padHour(n) {
  return String(n).padStart(2, '0');
}

const BUCKET_DEFS = {
  hourOfDay: {
    columns: Array.from({ length: 24 }, (_, i) => padHour(i)),
    normalize: (raw) => {
      const n = Number(raw);
      if (!Number.isFinite(n) || n < 0 || n > 23) return null;
      return padHour(Math.trunc(n));
    },
  },
  dayOfWeek: {
    // EXTRACT(ISODOW): 1 = Lunes ... 7 = Domingo.
    columns: DAY_OF_WEEK_NAMES_ES.slice(1),
    normalize: (raw) => {
      const n = Number(raw);
      if (!Number.isFinite(n) || n < 1 || n > 7) return null;
      return DAY_OF_WEEK_NAMES_ES[Math.trunc(n)];
    },
  },
  monthOfYear: {
    columns: MONTH_NAMES_ES.slice(1),
    normalize: (raw) => {
      const n = Number(raw);
      if (!Number.isFinite(n) || n < 1 || n > 12) return null;
      return MONTH_NAMES_ES[Math.trunc(n)];
    },
  },
  dayOfMonth: {
    columns: Array.from({ length: 31 }, (_, i) => String(i + 1)),
    normalize: (raw) => {
      const n = Number(raw);
      if (!Number.isFinite(n) || n < 1 || n > 31) return null;
      return String(Math.trunc(n));
    },
  },
};

/** Buckets con un conjunto de columnas fijo y conocido: los unicos donde pivotear a formato ancho tiene sentido. */
export const PIVOTABLE_DATE_BUCKETS = Object.keys(BUCKET_DEFS);

/**
 * Busca, entre las columnas guardadas del gráfico, la que se marcó para
 * pivotear (dateBucket en PIVOTABLE_DATE_BUCKETS + pivotHour true) y la otra
 * columna Fila (el grupo, ej. Sitio). Devuelve null si no aplica.
 *
 * No resuelve aqui la columna Valor: en modo Clave/valor, el editor muestra
 * la clave y el valor como dos filas separadas, pero por dentro siguen
 * siendo la MISMA columna (un solo objeto en tableColumns con
 * objectKvFields), asi que no hay forma confiable de distinguir cual alias
 * corresponde al numero solo mirando la config. En vez de adivinar,
 * pivotRowsByDateBucket la detecta directo de los datos.
 *
 * resolvedAliases debe venir de resolvePlainColumnAliases(tableColumns)
 * (LiveDatasetTable.js): es la unica fuente confiable de la clave real que
 * usa cada columna en las filas devueltas por la consulta (col.alias, si el
 * usuario le puso un Nombre propio, o el alias tecnico auto-generado si no
 * — usar col.alias || col.column a secas no sirve, no coincide con el
 * alias real cuando la columna no tiene Nombre puesto).
 */
export function findDateBucketPivotConfig(tableColumns, resolvedAliases) {
  const cols = Array.isArray(tableColumns) ? tableColumns : [];
  const aliases = Array.isArray(resolvedAliases) ? resolvedAliases : [];
  const bucketIdx = cols.findIndex((c) => BUCKET_DEFS[c?.dateBucket] && c?.pivotHour === true);
  if (bucketIdx < 0) return null;
  const bucketType = cols[bucketIdx].dateBucket;
  const bucketKey = aliases[bucketIdx];
  const rowIdx = cols.findIndex(
    (c, i) => c && i !== bucketIdx && c.role !== 'value' && c.role !== 'filter' && aliases[i] !== bucketKey
  );
  if (rowIdx < 0) return null;
  const rowKey = aliases[rowIdx];
  if (!bucketKey || !rowKey) return null;
  return { bucketKey, rowKey, bucketType };
}

/**
 * Config del pivote para un gráfico guardado (tableColumns + resolvePlainColumnAliases
 * ya resueltos por el caller). Igual que findDateBucketPivotConfig, pero si las filas ya
 * traidas son el dataset sintético de la comparación por segunda fecha
 * (periodo_label/serie/valor, ver computeCustomDateComparison en el servidor) en vez del
 * resultado normal de la consulta, el pivote tiene que agrupar por esas claves fijas en
 * vez de las que arma findDateBucketPivotConfig a partir de tableColumns.
 *
 * Se detecta por la FORMA de las filas ya traidas (rows), no por si el gráfico tiene la
 * comparación configurada en el spec: el spec puede tener "Comparar con fecha" armado
 * pero, si esa fecha quedó vacía en esta consulta puntual, el servidor devuelve filas
 * normales igual (opt-in). Guiarse solo por el spec pivotearía por claves que esas filas
 * no tienen, dejando la tabla vacía. Mismo criterio que ya usa comparisonMapping en
 * ChartGrid.js para gráficos de barra/línea.
 */
export function dateBucketPivotConfigForChart(tableColumns, resolvedAliases, rows) {
  const baseConfig = findDateBucketPivotConfig(tableColumns, resolvedAliases);
  if (!baseConfig) return null;
  const firstRow = Array.isArray(rows) ? rows[0] : null;
  const looksLikeCustomDateCompare = Boolean(
    firstRow &&
      Object.prototype.hasOwnProperty.call(firstRow, 'periodo_label') &&
      Object.prototype.hasOwnProperty.call(firstRow, 'serie')
  );
  if (looksLikeCustomDateCompare) {
    // El dataset sintetico de comparacion siempre usa 'serie' como clave de fila (ver
    // computeCustomDateComparison en el servidor); rowHeaderLabel conserva el nombre real
    // de la columna que se esta agrupando (ej. "Sitio") para mostrarlo como encabezado en
    // vez de esa clave tecnica.
    return {
      ...baseConfig,
      bucketKey: 'periodo_label',
      rowKey: 'serie',
      rowHeaderLabel: baseConfig.rowKey,
    };
  }
  return baseConfig;
}

/**
 * Entre las claves de una fila que no son rowKey ni bucketKey, elige la que
 * tenga valores numericos de verdad (no texto como "incoming"/"outgoing").
 * Mira varias filas por si la primera trae un caso raro (null, string vacio).
 */
function detectValueKey(rows, rowKey, bucketKey) {
  const sample = rows.slice(0, 20);
  const candidates = new Set();
  for (const r of sample) {
    if (!r || typeof r !== 'object') continue;
    for (const k of Object.keys(r)) {
      if (k !== rowKey && k !== bucketKey) candidates.add(k);
    }
  }
  let best = null;
  let bestCount = 0;
  for (const k of candidates) {
    let count = 0;
    for (const r of sample) {
      const v = r?.[k];
      if (v !== null && v !== undefined && v !== '' && Number.isFinite(Number(v))) count += 1;
    }
    if (count > bestCount) {
      best = k;
      bestCount = count;
    }
  }
  return best;
}

/**
 * Orden explicito de columnas para DataTable (visibleColumns): el grupo
 * primero y luego las columnas del bucket en su orden natural (00..23,
 * Lunes..Domingo, 1..31 o Enero..Diciembre). Sin esto, JS reordena solo las
 * claves que parecen indices enteros (ej. "10".."23") antes que el resto al
 * leer Object.keys(), dejando el encabezado desordenado.
 */
export function dateBucketPivotColumnOrder(rowKey, bucketType) {
  const def = BUCKET_DEFS[bucketType];
  return [rowKey, ...(def ? def.columns : [])];
}

/**
 * rows: filas largas [{ [rowKey]: 'Temuco', [bucketKey]: 3, [valueKey]: 12 }, ...]
 * Devuelve filas anchas [{ [rowKey]: 'Temuco', 'Miércoles': 12, ... }, ...],
 * ordenadas por el valor del grupo, con columnas del bucket sin datos en 0.
 * config: { bucketKey, rowKey, bucketType } (de findDateBucketPivotConfig).
 * valueKey se detecta de los datos si no se pasa explicito, ver detectValueKey.
 */
export function pivotRowsByDateBucket(rows, config) {
  if (!Array.isArray(rows) || !config) return rows || [];
  const { bucketKey, rowKey, bucketType } = config;
  const def = BUCKET_DEFS[bucketType];
  if (!def) return [];
  const valueKey = config.valueKey || detectValueKey(rows, rowKey, bucketKey);
  if (!valueKey) return [];
  const byRow = new Map();
  for (const r of rows) {
    const rowLabel = r?.[rowKey];
    if (rowLabel == null || String(rowLabel).trim() === '') continue;
    const bucketLabel = def.normalize(r?.[bucketKey]);
    if (bucketLabel == null) continue;
    if (!byRow.has(rowLabel)) {
      const base = { [rowKey]: rowLabel };
      for (const col of def.columns) base[col] = 0;
      byRow.set(rowLabel, base);
    }
    const value = Number(r?.[valueKey]);
    const target = byRow.get(rowLabel);
    target[bucketLabel] += Number.isFinite(value) ? value : 0;
  }
  return Array.from(byRow.values()).sort((a, b) =>
    String(a[rowKey]).localeCompare(String(b[rowKey]))
  );
}
